<template>
  <UInputMenu
    v-model:search-term="query"
    v-bind="absentAwareProps"
    :items="menuItems"
    value-key="value"
    label-key="label"
    :placeholder="$t('common.timezone.searchTimezone')"
    :disabled="disabled"
    variant="none"
    class="w-full"
    :ui="menuUi"
    ignore-filter
    open-on-click
    @update:model-value="onSelect"
  >
    <!-- Slots, not the vendor's icon props: those resolve through @nuxt/icon, and this app ships no Iconify collection. -->
    <template #trailing>
      <ChevronUpDownIcon
        class="dark:text-content-level-400 size-5 sm:size-4 text-content-level-500"
        aria-hidden="true"
      />
    </template>

    <template #item-label="{ item }">
      <span :class="isSelectedItem(item) ? 'font-semibold' : 'font-normal'">{{
        itemLabel(item)
      }}</span>
    </template>

    <template #item-trailing="{ item }">
      <CheckIcon
        v-if="isSelectedItem(item)"
        class="size-5 text-primary"
        aria-hidden="true"
      />
    </template>

    <template #empty>{{ $t('common.timezone.noTimezoneFound') }}</template>
  </UInputMenu>
</template>

<script setup lang="ts">
import { CheckIcon } from '@heroicons/vue/20/solid'
import { ChevronUpDownIcon } from '@heroicons/vue/16/solid'
import { ref, computed } from 'vue'
import UInputMenu from '@nuxt/ui/components/InputMenu.vue'
import type { InputMenuItem } from '@nuxt/ui'
import { useI18n } from 'vue-i18n'

import { useTimezone } from '~/composables/useTimezone'

import { fieldControlUi } from '~/design-system/base/fieldControlUi'
import { omitEmptyStringProps } from '~/design-system/base/omitEmptyStringProps'
import type { TimezoneOption } from '@/composables/TimezoneOption'

interface Props {
  /** Optional: the browser-detected timezone, pinned at top of list */
  detectedTimezone?: string
  /**
   * Id for the input. Bind the id a FormField hands its control slot;
   * empty leaves the surrounding field to supply one.
   */
  id?: string | undefined
  /** Paints the error state. Bind the FormField control slot's `invalid`. */
  invalid?: boolean
  /** Prevents user interaction when true. */
  disabled?: boolean
}

const props = defineProps<Props>()

const modelValue = defineModel<string>()

const { getTimezoneOptions, formatTimezoneLabel } = useTimezone()
const { t } = useI18n()

const query = ref('')

const selectedValue = computed(() => modelValue.value ?? '')

/** `modelValue=""`/`id=""` read as real values to the vendor, not "absent". */
const absentAwareProps = computed(() => ({
  ...omitEmptyStringProps({ id: props.id ?? '', modelValue: selectedValue.value }),
  'aria-invalid': props.invalid ? true : undefined
}))

const menuUi = computed(() => ({
  base: [
    fieldControlUi.control.base,
    props.invalid ? fieldControlUi.control.outlineInvalid : fieldControlUi.control.outline,
    'pe-10'
  ],
  itemTrailingIcon: 'hidden'
}))

const allOptions = computed<TimezoneOption[]>(() => getTimezoneOptions(props.detectedTimezone))

/** The vendor writes the selected label into the input; that label alone means nothing typed. */
const normalizedQuery = computed(() => {
  const typed = query.value.trim().toLowerCase()
  return query.value === formatTimezoneLabel(selectedValue.value) ? '' : typed
})

const filteredOptions = computed<TimezoneOption[]>(() => {
  const typed = normalizedQuery.value
  if (typed === '') return allOptions.value

  return allOptions.value.filter(
    (opt) => opt.value.toLowerCase().includes(typed) || opt.label.toLowerCase().includes(typed)
  )
})

const isDetectedFirst = computed(
  () =>
    props.detectedTimezone !== undefined &&
    props.detectedTimezone !== '' &&
    normalizedQuery.value === '' &&
    filteredOptions.value[0]?.value === props.detectedTimezone
)

const menuItems = computed<InputMenuItem[]>(() => {
  const [first, ...rest] = filteredOptions.value
  if (first === undefined || !isDetectedFirst.value) return filteredOptions.value

  return [
    { label: t('common.timezone.detected'), type: 'label' },
    first,
    { type: 'separator' },
    ...rest
  ]
})

const isSelectedItem = (item: InputMenuItem): boolean =>
  typeof item === 'object' && item !== null && item['value'] === selectedValue.value

const itemLabel = (item: InputMenuItem): string =>
  typeof item === 'object' && item !== null && typeof item.label === 'string' ? item.label : ''

const hasSelectedTimezone = (value: string | undefined): value is string => value !== undefined

function onSelect(value: string | undefined) {
  if (hasSelectedTimezone(value)) modelValue.value = value
  query.value = ''
}
</script>
